import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AutosizeModule } from 'ngx-autosize';
import { ApiService } from '../../../../core/services/api.service';
import { GraphNode } from '../../../../core/models/chat.models';

const LABEL_ABBREVIATIONS: Record<string, string> = { aws: 'AWS', s3: 'S3', id: 'ID' };
const EPOCH_MS_SENTINEL = 10_000_000_000_000;

interface HistoryItem {
  timestamp: string;
  oldValue: string | null;
  newValue: string | null;
  updatedBy: string;
  elementId?: string;
  batchHadFailures?: boolean;
}

@Component({
  selector: 'app-node-details-sidebar',
  imports: [CommonModule, FormsModule, AutosizeModule],
  templateUrl: './node-details-sidebar.component.html',
  styleUrls: ['./node-details-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NodeDetailsSidebarComponent implements OnChanges {
  @Input() node: GraphNode | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() suggestChunkEdits = new EventEmitter<{ nodeId: string, oldValue: string, label: string, desiredChange: string }>();
  @Output() editChunksDirectly = new EventEmitter<{ nodeId: string, originalValue: string, editedValue: string }>();
  @Output() errorEvent = new EventEmitter<string>();

  isEditing = false;
  editedValue = '';
  desiredChange = '';

  history: HistoryItem[] = [];
  isHistoryLoading = false;
  openAccordion: number | null = null;
  originalNodeId: string = '';

  // Pre-computed properties replacing getters
  cleanLabel = '';
  propertyEntries: { key: string, value: any, formattedKey: string, formattedValue: string }[] = [];
  isChunkNode = false;
  isEntityNode = false;
  isSourceNode = false;
  isKnowledgeNode = false;
  canSuggestEdit = false;
  isConfirmDisabled = false;

  constructor(private apiService: ApiService, private cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['node']) {
      const node = changes['node'].currentValue;
      this.updateOriginalNodeId(); // Update ID
      if (node) {
        this.resetState();
        this.computeProperties(); // Recompute properties based on new node
        if (this.isChunkNode) {
          this.fetchHistory(this.originalNodeId);
        }
      } else {
        this.editedValue = '';
        this.computeProperties(); // Clear properties if node is null
      }
    }
  }

  computeProperties(): void {
    this.cleanLabel = this.node?.label.replace(/__/g, '') || '';
    this.isChunkNode = this.node?.label.toUpperCase() === '__CHUNK__';
    this.isEntityNode = this.node?.label.toUpperCase() === '__ENTITY__';
    this.isSourceNode = this.node?.label.toUpperCase() === '__SOURCE__';

    const label = this.node?.label.toUpperCase();
    this.isKnowledgeNode = ['__FACT__', '__STATEMENT__', '__TOPIC__'].includes(label || '');
    this.canSuggestEdit = this.isChunkNode || this.isKnowledgeNode;

    this.computePropertyEntries();
    this.computeIsConfirmDisabled();
  }

  computePropertyEntries(): void {
    if (!this.node || !this.node.properties) {
      this.propertyEntries = [];
      return;
    }

    const props = this.node.properties;
    const isEntity = this.isEntityNode;
    const toEntry = ([key, value]: [string, any]) => ({
      key,
      value,
      formattedKey: this.formatLabel(key),
      formattedValue: this.formatPropertyValue(value),
    });

    if (this.isSourceNode) {
      this.propertyEntries = Object.entries(props)
        .filter(([_, v]) => !this.isEmptyValue(v))
        .map(toEntry);
    } else {
      const allowed = new Set(isEntity ? ['value', 'class'] : ['value']);
      this.propertyEntries = Object.entries(props).filter(([k, v]) => {
        if (!allowed.has(k)) return false;
        if (k === 'value') {
          return (this.isChunkNode && this.isEditing) ? true : !this.isEmptyValue(v);
        }
        return !this.isEmptyValue(v);
      }).map(toEntry);
    }
  }

  formatLabel(key: string): string {
    let s = key.replace(/^_+|_+$/g, '');
    s = s.replace(/_+/g, ' ');
    s = s.replace(/([a-z])([A-Z])/g, '$1 $2');
    s = s.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
    return s.split(' ').filter(Boolean).map(word => {
      const lower = word.toLowerCase();
      return LABEL_ABBREVIATIONS[lower] ?? (word.charAt(0).toUpperCase() + word.slice(1));
    }).join(' ');
  }

  formatPropertyValue(value: any): string {
    if (value == null) return '';
    if (typeof value === 'number' || (typeof value === 'string' && /^\d{13}$/.test(String(value)))) {
      const num = Number(value);
      if (num >= 1e12 && num <= 1e14) {
        if (num >= EPOCH_MS_SENTINEL) return 'No expiry';
        return new Date(num).toLocaleString();
      }
    }
    return String(value);
  }

  computeIsConfirmDisabled(): void {
    const originalValue = this.node?.properties?.['value'] || '';
    if (this.isChunkNode && this.editedValue === originalValue) {
      this.isConfirmDisabled = true;
    } else if (this.isKnowledgeNode && !this.desiredChange.trim()) {
      this.isConfirmDisabled = true;
    } else {
      this.isConfirmDisabled = false;
    }
  }

  updateOriginalNodeId(): void {
    this.originalNodeId = this.node?.properties?.['originalId'] || this.node?.id || '';
  }

  resetState(): void {
    this.editedValue = this.node?.properties?.['value'] || '';
    this.desiredChange = '';
    this.setIsEditing(false);
    this.history = [];
    this.openAccordion = null;
  }

  setIsEditing(value: boolean): void {
    this.isEditing = value;
    if (value) {
      // Re-initialize edited value on edit start
      this.editedValue = this.node?.properties?.['value'] || '';
    }
    this.computePropertyEntries(); // Recompute properties dependent on isEditing
  }

  fetchHistory(nodeId: string): void {
    this.isHistoryLoading = true;
    this.apiService.getFullChunkHistory(nodeId).subscribe({
      next: (data) => {
        this.history = (data || []).reverse();
        this.isHistoryLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to load history', err);
        this.errorEvent.emit('Failed to load history');
        this.history = [];
        this.isHistoryLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  isEmptyValue(v: any): boolean {
    if (v == null) return true;
    if (typeof v === 'string') return v.trim() === '';
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'object') return Object.keys(v).length === 0;
    return false;
  }

  toggleAccordion(index: number): void {
    this.openAccordion = this.openAccordion === index ? null : index;
  }

  formatDate(isoString: string): string {
    if (!isoString) return 'N/A';
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return 'Invalid Date';
    }
  }

  handleOpenConfirmDialog(): void {
    if (!this.node) return;

    if (this.isChunkNode) {
      this.editChunksDirectly.emit({
        nodeId: this.originalNodeId,
        originalValue: this.node.properties['value'] || '',
        editedValue: this.editedValue
      });
    } else if (this.isKnowledgeNode) {
      this.suggestChunkEdits.emit({
        nodeId: this.originalNodeId,
        oldValue: this.node.properties['value'] || '',
        label: this.node.label,
        desiredChange: this.desiredChange
      });
      // Reset edit mode
      this.setIsEditing(false);
      this.desiredChange = '';
    }
  }

  cancelEdit(): void {
    this.setIsEditing(false);
    this.editedValue = this.node?.properties['value'] || '';
    this.desiredChange = '';
    this.computeIsConfirmDisabled();
  }

  trackByKey(index: number, entry: any): string {
    return entry.key;
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}
